import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, Phone } from 'lucide-react';
import { useT } from '../i18n/LangContext';
import { PHONE, PHONE_HREF } from '../data/content';

const DISMISS_KEY = 'rev-popup-dismissed';
const BOOKED_KEY = 'rev-quote-submitted';

export default function ScrollPopup() {
  const t = useT();
  const { pathname } = useLocation();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (pathname === '/quote') return;
    if (localStorage.getItem(DISMISS_KEY) || localStorage.getItem(BOOKED_KEY)) return;
    let triggered = false;
    let scrolledDeep = false;
    let timer;

    const onScroll = () => {
      const depth = window.scrollY / (document.body.scrollHeight - window.innerHeight || 1);
      if (depth > 0.45) scrolledDeep = true;
      if (scrolledDeep && !triggered) {
        triggered = true;
        timer = setTimeout(() => setShow(true), 4000);
        window.removeEventListener('scroll', onScroll);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    // fallback: 50s on any page without deep scroll
    const fallback = setTimeout(() => {
      if (!triggered) { triggered = true; setShow(true); }
    }, 50000);
    return () => {
      window.removeEventListener('scroll', onScroll);
      clearTimeout(timer);
      clearTimeout(fallback);
    };
  }, [pathname]);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setShow(false);
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div data-testid="scroll-popup" initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.97 }}
          transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          className="fixed bottom-24 left-4 right-4 z-50 mx-auto max-w-md rounded-2xl border border-gold/30 bg-coal p-7 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)] md:bottom-8 md:left-8 md:right-auto"
          role="dialog" aria-modal="false" aria-label={t['pop.title']}>
          <button data-testid="scroll-popup-close" onClick={dismiss} aria-label="Close"
            className="absolute right-4 top-4 rounded-full p-1.5 text-silver transition-colors hover:bg-white/10 hover:text-white">
            <X className="h-4 w-4" />
          </button>
          <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-gold">
            <span className="inline-block h-2 w-2 bg-gold" /> {t['pop.eyebrow']}
          </span>
          <h3 className="mt-3 font-serif text-2xl font-semibold leading-snug text-white">{t['pop.title']}</h3>
          <p className="mt-2 text-sm leading-relaxed text-silver">{t['pop.body']}</p>
          <div className="mt-5 flex flex-col gap-3">
            <Link data-testid="scroll-popup-quote" to="/quote" onClick={dismiss} className="gold-btn w-full">
              {t['pop.cta']} <ArrowRight className="h-4 w-4" />
            </Link>
            <a data-testid="scroll-popup-call" href={PHONE_HREF}
              className="flex items-center justify-center gap-2 text-sm font-bold text-gold transition-colors hover:text-gold-light">
              <Phone className="h-4 w-4" /> {t['pop.call']} {PHONE}
            </a>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
